import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../css/Home.css";

// Safely resolve assets from src or public
function resolveAsset(name) {
  try {
    const req = require.context("../", true, /\.(svg|png|jpe?g)$/);
    const match = req
      .keys()
      .find((k) => k.toLowerCase().includes(name.toLowerCase()));
    if (match) {
      const res = req(match);
      return res.default || res;
    }
  } catch (e) {
    // ignore
  }
  return `${process.env.PUBLIC_URL || ""}/${name}`;
}

const logoSvg = resolveAsset("logo.svg");
const homeNewsPng = resolveAsset("home-news.png");
const committeePng = resolveAsset("team-luisa.png");
const homeBanner = resolveAsset("home-banner.jpg");

function Home() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    role: "",
    email: "",
  });
  const [status, setStatus] = useState({ loading: false, success: null, message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email) return;

    // mark as submitted immediately to replace form with thank-you UI
    setSubmitted(true);

    setStatus({ loading: true, success: null, message: "" });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const res = await fetch(`${apiUrl}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        setStatus({
          loading: false,
          success: true,
          message: "Check your inbox for Oxford Sympa's confirmation email!",
        });
        setFormData({ firstName: "", lastName: "", role: "", email: "" });
      } else {
        setStatus({
          loading: false,
          success: false,
          message: data.error || "Subscription failed. Please try again.",
        });
      }
    } catch (err) {
      // Fallback: direct fallback to Oxford Sympa mailto if API unreachable
      window.location.href = `mailto:ossce-subscribe@maillist.ox.ac.uk?subject=SUBSCRIBE%20ossce%20${encodeURIComponent(
        `${formData.firstName} ${formData.lastName}`.trim()
      )}&body=SUB%20ossce`;
      setStatus({
        loading: false,
        success: true,
        message: "Sending us an email to confirm subscription...",
      });
    }
  };

  return (
    <div className="home-page-container">
      <div className="home-content-wrapper">
        {/* Hero Section */}
        <div className="home-hero-section">
          <div
            className="home-hero-banner"
            style={{ backgroundImage: `url(${homeBanner})` }}
          />{" "}

          <div className="home-hero-text">
            <span className="home-eyebrow">WELCOME</span>
            <h1 className="home-hero-title">
              Research in Conversation
              <br />
              {/* Enspiring Dialogue */}
            </h1>

            <p className="home-body-p home-welcome">
              Welcome to the Oxford Society for Science Communication and
              Engagement
            </p>

            <p className="home-body-p">
              At OSSCE, we recognise that science is often conducted within a
              fragmented system. We believe it is crucial for science to
              effectively communicate its purpose and engage with a diverse
              range of stakeholders, including policymakers, the public, and
              fellow scientists.
            </p>

            <p className="home-body-p">
              Our mission is to serve as an interdisciplinary platform that
              fosters dialogue and builds bridges, encouraging collaboration and
              mutual learning to strengthen the connections between these vital
              groups.
            </p>

            <div>
              <Link to="/about" className="home-btn-yellow">
                Learn more about us!
              </Link>
            </div>
          </div>
        </div>

        {/* Feature Cards: Newsletter, Career, Training */}
        <div className="home-feature-cards">
          <div className="home-feature-card">
            <div className="home-feature-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
              </svg>
            </div>
            <h3 className="home-feature-title">Newsletter</h3>
            <p className="home-feature-desc">
              Get the latest updates on exciting science communication and
              engagement events, trainings, and projects across the university.
            </p>
          </div>

          <div className="home-feature-card">
            <div className="home-feature-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
              </svg>
            </div>
            <h3 className="home-feature-title">Career</h3>
            <p className="home-feature-desc">
              Connect with experts and explore career opportunities in science
              policy, communication, and engagement, both inside and outside
              academia.
            </p>
          </div>

          <div className="home-feature-card">
            <div className="home-feature-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </div>
            <h3 className="home-feature-title">Training</h3>
            <p className="home-feature-desc">
              Gain high-quality science communication training and hands-on
              experience by getting involved in engagement projects with our
              partners.
            </p>
          </div>
        </div>

        {/* Get To Know Us */}
        <div className="home-get-to-know">
          <span className="home-eyebrow center">GET TO KNOW US</span>
          <div className="home-nav-cards">
            {/* Committee Card -> /about */}
            <Link to="/about" className="home-nav-card">
              <div className="home-nav-thumb">
                <img
                  src={committeePng}
                  alt="Committee"
                  className="home-nav-thumb-img"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    if (e.currentTarget.nextSibling) {
                      e.currentTarget.nextSibling.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="home-nav-thumb-fallback"
                  style={{
                    display: "none",
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: "2px solid #5a5a8a",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, #f7d6a5, #e08f86, #7cb3d4)",
                  }}
                >
                  <span
                    style={{
                      fontSize: "7px",
                      fontWeight: "900",
                      color: "#1a1a3a",
                      letterSpacing: "0.5px",
                    }}
                  >
                    OSSCE
                  </span>
                </div>
              </div>
              <div className="home-nav-info">
                <span className="home-nav-title">
                  Meet the
                  <br />
                  Committee
                </span>
                <span className="home-nav-subtitle">Who is responsible?</span>
              </div>
            </Link>

            {/* Programme Card -> /program */}
            <Link to="/program" className="home-nav-card">
              <div className="home-nav-thumb">
                <img
                  src={logoSvg}
                  alt="OSSCE Logo"
                  className="home-nav-thumb-img"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    if (e.currentTarget.nextSibling) {
                      e.currentTarget.nextSibling.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="home-nav-thumb-fallback"
                  style={{
                    display: "none",
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: "2px solid #5a5a8a",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(135deg, #f7d6a5, #e08f86, #7cb3d4)",
                  }}
                >
                  <span
                    style={{
                      fontSize: "7px",
                      fontWeight: "900",
                      color: "#1a1a3a",
                      letterSpacing: "0.5px",
                    }}
                  >
                    OSSCE
                  </span>
                </div>
              </div>
              <div className="home-nav-info">
                <span className="home-nav-title">Programme</span>
                <span className="home-nav-subtitle">Check out our events!</span>
              </div>
            </Link>

            {/* News Card -> /news */}
            <Link to="/news" className="home-nav-card">
              <div className="home-nav-thumb">
                <img
                  src={homeNewsPng}
                  alt="News"
                  className="home-nav-thumb-img"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    if (e.currentTarget.nextSibling) {
                      e.currentTarget.nextSibling.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="home-nav-thumb-fallback"
                  style={{
                    display: "none",
                    position: "relative",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="34"
                    height="34"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="1.6"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                  </svg>
                  <span
                    style={{
                      position: "absolute",
                      background: "#f59e0b",
                      color: "#111",
                      fontSize: "7px",
                      fontWeight: "800",
                      padding: "1px 3px",
                      borderRadius: "2px",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                    }}
                  >
                    NEWS
                  </span>
                </div>
              </div>
              <div className="home-nav-info">
                <span className="home-nav-title">News</span>
                <span className="home-nav-subtitle">Stay updated!</span>
              </div>
            </Link>
          </div>
        </div>

        {/* Interested Section */}
        <div className="home-interested-section">
          <span
            className="home-eyebrow center"
            style={{ color: "#e7624b", textTransform: "none" }}
          >
            Interested?
          </span>
          <h2 className="home-section-title">Let's keep in touch.</h2>
          <p className="home-interested-desc">
            Anyone interested in science communication, whether with experience
            or not, is welcome to get involved! Curiosity is all you need—join
            our free events, become a member to enjoy exclusive benefits, and
            stay informed by subscribing to our newsletter. We offer a
            supportive community for everyone eager to learn and engage!
          </p>
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLScqzCl4My7Svw8Kyrk4vuNGwCFFi9FXcjQOT33SKAQ2dKqkpQ/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="home-btn-yellow"
          >
            Become a Member (free)
          </a>
        </div>

        {/* Newsletter Signup Box */}
        <div className="home-newsletter-box">
          {!submitted ? (
            <>
              <div className="home-newsletter-header">Subscribe to Our Newsletter</div>
              <form onSubmit={handleSubmit}>
                <div className="home-form-grid">
                  <input
                    type="text"
                    name="firstName"
                    className="home-input"
                    placeholder="FIRST NAME"
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  <input
                    type="text"
                    name="lastName"
                    className="home-input"
                    placeholder="LAST NAME"
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  {/* <input
                    type="text"
                    name="role"
                    className="home-input"
                    placeholder="ROLE"
                    value={formData.role}
                    onChange={handleChange}
                  /> */}
                  <input
                    type="email"
                    name="email"
                    className="home-input"
                    placeholder="EMAIL"
                    required
                    value={formData.email}
                    onChange={handleChange}
                  />
                  <button
                    type="submit"
                    className="home-submit-btn"
                    disabled={status.loading}
                  >
                    {status.loading ? "SUBMITTING..." : "SUBMIT"}
                  </button>
                </div>
                {status.message && (
                  <p
                    style={{
                      marginTop: "12px",
                      fontSize: "12px",
                      fontWeight: "600",
                      textAlign: "center",
                      color: status.success ? "#2e7d32" : "#d32f2f",
                    }}
                  >
                    {status.message}
                  </p>
                )}
              </form>
            </>
          ) : (
            <div style={{ padding: '20px 8px', textAlign: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Thank you for subscribing!</h3>
              {status.message && (
                <p style={{ marginTop: '8px', fontSize: '13px', color: status.success ? '#2e7d32' : '#d32f2f' }}>
                  {status.message}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Give Feedback Section */}
        <div className="home-feedback-section" id="contact-us">
          {/* <p className="home-feedback-prompt">Want to get in touch?</p> */}
          <h2 className="home-section-title" style={{ margin: "4px 0 12px 0" }}>
            Want to get in touch?
          </h2>
          <p className="home-feedback-text">
            Please provide feedback using our feedback form or reach out to
            contact@ossce.org.uk
            <br />
            <br />
            We look forward to hearing from you!
          </p>
          <a
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            className="home-btn-outline"
          >
            Give Feedback!
          </a>
        </div>
      </div>
    </div>
  );
}

export default Home;
